import { ColetaVtmeLado, ColetaVtmePessoa } from '../../types/coleta';
import { ItemResumoQualidade } from './coletaResumoQualidade';
import { REGRAS_VTME } from './descricaoRegraQualidade';

type ChaveVtme = keyof typeof REGRAS_VTME;
type RegraVtme = { rotulo: string; descricao: string };
type TipoPedido = 'Aditivo' | 'Renegociação';

export const RESULTADOS_ID_PEDIDO = new Set([
  'id_invalido',
  'numero_invalido',
  'nao_encontrado_portais',
  'sem_login_unity',
  'login_unity_falhou',
]);

export function textoIdPedido(
  lado: ColetaVtmeLado | null | undefined,
  tipo?: TipoPedido,
): string {
  const id = (lado?.id || '').trim();
  const pedido = tipo && id ? `${tipo} ${id}` : (id || tipo || 'pedido');
  const codigo = lado?.resultado || '';
  if (codigo === 'id_invalido' || codigo === 'numero_invalido') {
    return `No pedido ${pedido} o id preenchido no Gettel está errado.`;
  }
  if (codigo === 'nao_encontrado_portais') {
    return `Buscamos o pedido ${id} no VTME novo e no VTME antigo e não encontramos um pedido desse CNPJ. Revise o id do pedido preenchido no Gettel.`;
  }
  if (codigo === 'sem_login_unity') {
    return `Buscamos o pedido ${id} no VTME novo e não encontramos um pedido desse CNPJ. O VTME antigo não foi consultado porque não há login.`;
  }
  if (codigo === 'login_unity_falhou') {
    return `Buscamos o pedido ${id} no VTME novo e não encontramos um pedido desse CNPJ. O login do VTME antigo não entrou.`;
  }
  return '';
}

function itemIdPedido(lado: ColetaVtmeLado | null | undefined, tipo?: TipoPedido): ItemResumoQualidade {
  return {
    rotulo: 'Id do pedido',
    texto: textoIdPedido(lado, tipo),
    tom: 'divergente',
    descricao: 'O id do pedido preenchido no Gettel precisa ser o número do pedido no VTME.',
  };
}

const CHAVES: ChaveVtme[] = [
  'negociacao',
  'cnpj',
  'administrador',
  'representantes',
  'autorizados',
  'linhas',
];

function juntar(itens: string[]): string {
  if (itens.length <= 1) return itens[0] || '';
  if (itens.length === 2) return `${itens[0]} e ${itens[1]}`;
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

function item(regra: RegraVtme, ok: boolean | undefined, textoOk: string, textoNao: string): ItemResumoQualidade {
  if (ok == null) {
    return { rotulo: regra.rotulo, texto: 'Ainda sem conferência.', tom: 'neutro', descricao: regra.descricao };
  }
  return {
    rotulo: regra.rotulo,
    texto: ok ? textoOk : textoNao,
    tom: ok ? 'ok' : 'divergente',
    descricao: regra.descricao,
  };
}

function autorizadosNaoAplicavel(lado: ColetaVtmeLado | null | undefined): boolean {
  const regra = lado?.conferencias?.autorizados;
  return regra?.detalhe === 'nao_aplicavel' && regra.ok !== false;
}

function okDaRegra(lado: ColetaVtmeLado | null | undefined, chave: ChaveVtme, mesmoPedido: boolean): boolean | undefined {
  if (chave === 'autorizados' && autorizadosNaoAplicavel(lado)) return undefined;
  if (chave === 'negociacao' && mesmoPedido) return false;
  if (chave === 'cnpj') return lado?.conferencias?.cnpj?.ok ?? lado?.cnpjOk ?? undefined;
  const ok = lado?.conferencias?.[chave]?.ok;
  return ok == null ? undefined : ok;
}

function chavesDoResumo(
  aditivo?: ColetaVtmeLado | null,
  renegociacao?: ColetaVtmeLado | null,
): ChaveVtme[] {
  return CHAVES.filter((chave) => {
    if (chave !== 'autorizados') return true;
    if (!renegociacao) return !autorizadosNaoAplicavel(aditivo);
    if (!aditivo) return !autorizadosNaoAplicavel(renegociacao);
    return !(autorizadosNaoAplicavel(aditivo) && autorizadosNaoAplicavel(renegociacao));
  });
}

export function textoOkVtme(chave: ChaveVtme): string {
  if (chave === 'negociacao') return 'O tipo do pedido é o mesmo do contrato.';
  if (chave === 'cnpj') return 'O CNPJ do pedido é o mesmo do contrato.';
  if (chave === 'administrador') return 'O administrador do pedido é o responsável pela auditoria.';
  if (chave === 'representantes') return 'Os representantes legais são os mesmos no pedido e no contrato.';
  if (chave === 'autorizados') return 'Os autorizados a receber são os mesmos no pedido e no contrato.';
  return 'As linhas do pedido são as mesmas do contrato.';
}

function frasePedido(tipo: TipoPedido | undefined, id: string, resto: string): string {
  const pedido = (id || '').trim();
  const corpo = resto.trim();
  if (tipo && pedido) return `No pedido ${tipo} ${pedido} ${corpo}`;
  if (tipo) return `No pedido ${tipo} ${corpo}`;
  if (pedido) return `No pedido ${pedido} ${corpo}`;
  return corpo.charAt(0).toUpperCase() + corpo.slice(1);
}

function frasePedidos(idAditivo: string, idRenegociacao: string, corpo: string): string {
  const aditivo = (idAditivo || '').trim();
  const renegociacao = (idRenegociacao || '').trim();
  const resto = corpo.trim();
  if (aditivo && renegociacao) return `Nos pedidos Aditivo ${aditivo} e Renegociação ${renegociacao} ${resto}`;
  if (aditivo) return `Nos pedidos Aditivo ${aditivo} e Renegociação ${resto}`;
  if (renegociacao) return `Nos pedidos Aditivo e Renegociação ${renegociacao} ${resto}`;
  return `Nos pedidos Aditivo e Renegociação ${resto}`;
}

function corpoDaFrase(frase: string, tipo: TipoPedido, id: string): string | null {
  const pedido = (id || '').trim();
  const prefixos = [
    pedido ? `No pedido ${tipo} ${pedido} ` : '',
    `No pedido ${tipo} `,
    pedido ? `No pedido ${pedido} ` : '',
  ].filter(Boolean);
  const achado = prefixos.find((prefixo) => frase.startsWith(prefixo));
  return achado ? frase.slice(achado.length).trim() : null;
}

function formatarCnpj(valor?: string | null): string {
  const digitos = (valor || '').replace(/\D/g, '');
  if (digitos.length !== 14) return (valor || '').trim();
  return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}/${digitos.slice(8, 12)}-${digitos.slice(12)}`;
}

function nomesDivergentes(pessoas?: ColetaVtmePessoa[] | null): string[] {
  return (pessoas ?? [])
    .filter((pessoa) => (pessoa.conferencia || '').toLowerCase() === 'divergente')
    .map((pessoa) => (pessoa.nome || '').trim())
    .filter(Boolean);
}

function textoPessoas(
  tipo: TipoPedido | undefined,
  id: string,
  pessoas: ColetaVtmePessoa[] | null | undefined,
  singular: string,
  plural: string,
): string {
  const nomes = nomesDivergentes(pessoas);
  if (nomes.length === 1) {
    return frasePedido(tipo, id, `o ${singular} ${nomes[0]} diverge no nome, CPF ou telefone.`);
  }
  if (nomes.length > 1) {
    return frasePedido(tipo, id, `os ${plural} ${juntar(nomes)} divergem no nome, CPF ou telefone.`);
  }
  return frasePedido(tipo, id, `os ${plural} divergem no nome, CPF ou telefone.`);
}

function ehIccid(valor: string, tipo?: TipoPedido): boolean {
  const digitos = valor.replace(/\D/g, '');
  if (digitos.length >= 18 && digitos.startsWith('89')) return true;
  return tipo === 'Aditivo' && Boolean(digitos);
}

function partirLista(texto: string): string[] {
  return texto
    .split(/\s+e\s+|[,;]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseDetalheLinhas(detalhe?: string): {
  soContrato: string[];
  soVtme: string[];
  qtdContrato?: number;
  qtdVtme?: number;
} {
  const extra = (detalhe || '').trim();
  const soContrato: string[] = [];
  const soVtme: string[] = [];
  const gettel = extra.match(/só Gettel:\s*([^;]+)/i);
  const vtme = extra.match(/só VTME:\s*([^;]+)/i);
  if (gettel) soContrato.push(...partirLista(gettel[1]));
  if (vtme) soVtme.push(...partirLista(vtme[1]));

  const exclusivos = extra.matchAll(
    /(?:o |os )?(ICCIDs?|números?|numero)\s+([^;]+?)\s+(?:está|estão )?só no (contrato|VTME)/gi,
  );
  Array.from(exclusivos).forEach((match) => {
    const valores = partirLista(match[2]);
    if (match[3].toLowerCase() === 'contrato') soContrato.push(...valores);
    else soVtme.push(...valores);
  });

  const qtdCurta = extra.match(/^(\d+)\s*[×x]\s*(\d+)$/);
  const qtdFrase = extra.match(/contrato tem (\d+) linhas? e o VTME tem (\d+)/i);
  return {
    soContrato,
    soVtme,
    qtdContrato: qtdCurta ? Number(qtdCurta[1]) : qtdFrase ? Number(qtdFrase[1]) : undefined,
    qtdVtme: qtdCurta ? Number(qtdCurta[2]) : qtdFrase ? Number(qtdFrase[2]) : undefined,
  };
}

function unicos(valores: string[]): string[] {
  const vistos = new Set<string>();
  const saida: string[] = [];
  valores.forEach((valor) => {
    const chave = valor.replace(/\D/g, '') || valor;
    if (!valor || vistos.has(chave)) return;
    vistos.add(chave);
    saida.push(valor);
  });
  return saida;
}

function estaSo(singular: string, plural: string, valores: string[], lado: 'contrato' | 'VTME'): string {
  const sujeito = valores.length === 1
    ? `o ${singular} ${valores[0]}`
    : `os ${plural} ${juntar(valores)}`;
  const verbo = valores.length === 1 ? 'está' : 'estão';
  return `${sujeito} ${verbo} só no ${lado}`;
}

function qtdLinhas(quantidade: number): string {
  return quantidade === 1 ? '1 linha' : `${quantidade} linhas`;
}

function trechoQuantidade(contrato: number, vtme: number): string {
  return `o contrato tem ${qtdLinhas(contrato)} e o VTME tem ${vtme}`;
}

function textoLinhas(lado: ColetaVtmeLado | null | undefined, tipo?: TipoPedido): string {
  const id = lado?.id || '';
  const parsed = parseDetalheLinhas(lado?.conferencias?.linhas?.detalhe);
  const soOrigem = (lado?.soGettel?.length ? lado.soGettel : parsed.soContrato) ?? [];
  const soDestino = (lado?.soVtme?.length ? lado.soVtme : parsed.soVtme) ?? [];
  const soContrato = unicos(soOrigem.map((item) => item.trim()).filter(Boolean));
  const soVtme = unicos(soDestino.map((item) => item.trim()).filter(Boolean));
  const resultados = (lado?.linhas ?? []).map((linha) => (linha.resultado || '').toLowerCase());

  const iccidsContrato = soContrato.filter((item) => ehIccid(item, tipo));
  const iccidsVtme = soVtme.filter((item) => ehIccid(item, tipo));
  const numsContrato = soContrato.filter((item) => !ehIccid(item, tipo));
  const numsVtme = soVtme.filter((item) => !ehIccid(item, tipo));

  const partes: string[] = [];
  if (iccidsContrato.length) partes.push(estaSo('ICCID', 'ICCIDs', iccidsContrato, 'contrato'));
  if (iccidsVtme.length) partes.push(estaSo('ICCID', 'ICCIDs', iccidsVtme, 'VTME'));
  if (numsContrato.length) partes.push(estaSo('número', 'números', numsContrato, 'contrato'));
  if (numsVtme.length) partes.push(estaSo('número', 'números', numsVtme, 'VTME'));
  if (partes.length) return frasePedido(tipo, id, `${juntar(partes)}.`);

  const qtdC = parsed.qtdContrato;
  const qtdV = parsed.qtdVtme;
  if (qtdC != null && qtdV != null && qtdC !== qtdV) {
    return frasePedido(tipo, id, `${trechoQuantidade(qtdC, qtdV)}.`);
  }
  if (resultados.includes('ddd_diferente')) {
    return frasePedido(tipo, id, 'o DDD das linhas não coincide.');
  }
  if (resultados.includes('plano_diferente')) {
    return frasePedido(tipo, id, 'o plano das linhas não coincide.');
  }
  if (resultados.includes('valor_diferente')) {
    return frasePedido(tipo, id, 'o valor das linhas não coincide.');
  }
  if (qtdC != null && qtdV != null) {
    const motivo = tipo === 'Aditivo'
      ? 'ICCID, plano ou valor não coincidem'
      : 'os números não coincidem';
    return frasePedido(tipo, id, `${trechoQuantidade(qtdC, qtdV)}, mas ${motivo}.`);
  }
  const extra = (lado?.conferencias?.linhas?.detalhe || '').trim();
  if (extra) {
    const corpo = extra.replace(/\.$/, '');
    return frasePedido(tipo, id, `${corpo}.`);
  }
  return frasePedido(tipo, id, 'as linhas do pedido são diferentes do contrato.');
}

function textoNaoLado(
  lado: ColetaVtmeLado | null | undefined,
  chave: ChaveVtme,
  mesmoPedido: boolean,
  tipo?: TipoPedido,
): string {
  const id = lado?.id || '';
  if (chave === 'negociacao') {
    if (mesmoPedido) {
      const pedido = id.trim();
      return pedido
        ? `O Aditivo e a Renegociação apontam para o mesmo pedido ${pedido}.`
        : 'O Aditivo e a Renegociação apontam para o mesmo pedido.';
    }
    const tiposVtme = (lado?.conferencias?.negociacao?.detalhe || '').trim();
    const esperado = tipo || 'Aditivo ou Renegociação';
    if (tiposVtme && !/^\d+$/.test(tiposVtme)) {
      return frasePedido(tipo, id, `o VTME está como ${tiposVtme}, e o contrato espera ${esperado}.`);
    }
    return frasePedido(tipo, id, tipo
      ? `o tipo no VTME não é ${tipo}.`
      : 'o tipo do pedido é diferente do contrato.');
  }
  if (chave === 'cnpj') {
    const cnpj = formatarCnpj(lado?.cnpjPedido || lado?.conferencias?.cnpj?.detalhe);
    if (cnpj) {
      return frasePedido(tipo, id, `o CNPJ do VTME é ${cnpj}, diferente do contrato.`);
    }
    return frasePedido(tipo, id, 'o CNPJ do VTME é diferente do contrato.');
  }
  if (chave === 'administrador') {
    const nomes = nomesDivergentes(lado?.administrador);
    if (nomes.length === 1) {
      return frasePedido(tipo, id, `o administrador ${nomes[0]} não é o responsável pela auditoria.`);
    }
    if (nomes.length > 1) {
      return frasePedido(tipo, id, `os administradores ${juntar(nomes)} não são os responsáveis pela auditoria.`);
    }
    return frasePedido(tipo, id, 'o administrador não é o responsável pela auditoria.');
  }
  if (chave === 'representantes') {
    return textoPessoas(tipo, id, lado?.replegal, 'representante legal', 'representantes legais');
  }
  if (chave === 'autorizados') {
    return textoPessoas(tipo, id, lado?.autorizados, 'autorizado a receber', 'autorizados a receber');
  }
  return textoLinhas(lado, tipo);
}

export function resumoVtme(
  lado: ColetaVtmeLado | null | undefined,
  mesmoPedido: boolean,
  tipo?: TipoPedido,
): ItemResumoQualidade[] {
  if (RESULTADOS_ID_PEDIDO.has(lado?.resultado || '')) return [itemIdPedido(lado, tipo)];
  return chavesDoResumo(lado).map((chave) => item(
    REGRAS_VTME[chave],
    okDaRegra(lado, chave, mesmoPedido),
    textoOkVtme(chave),
    textoNaoLado(lado, chave, mesmoPedido, tipo),
  ));
}

export function resumoVtmeAgrupado(entrada: {
  aditivo?: ColetaVtmeLado | null;
  renegociacao?: ColetaVtmeLado | null;
  mesmoPedido: boolean;
}): ItemResumoQualidade[] {
  const aditivo = entrada.aditivo;
  const renegociacao = entrada.renegociacao;
  const especialAditivo = RESULTADOS_ID_PEDIDO.has(aditivo?.resultado || '');
  const especialReneg = RESULTADOS_ID_PEDIDO.has(renegociacao?.resultado || '');
  if (especialAditivo || especialReneg) {
    const itens: ItemResumoQualidade[] = [];
    if (aditivo && !especialAditivo) itens.push(...resumoVtme(aditivo, entrada.mesmoPedido, 'Aditivo'));
    if (renegociacao && !especialReneg) itens.push(...resumoVtme(renegociacao, entrada.mesmoPedido, 'Renegociação'));
    if (especialAditivo) itens.push(itemIdPedido(aditivo, 'Aditivo'));
    if (especialReneg) itens.push(itemIdPedido(renegociacao, 'Renegociação'));
    return itens;
  }
  if (aditivo && !renegociacao) return resumoVtme(aditivo, entrada.mesmoPedido, 'Aditivo');
  if (!aditivo && renegociacao) return resumoVtme(renegociacao, entrada.mesmoPedido, 'Renegociação');
  if (!aditivo || !renegociacao) return [];
  return chavesDoResumo(aditivo, renegociacao).map((chave) => agruparRegra(chave, aditivo, renegociacao, entrada.mesmoPedido));
}

function agruparRegra(
  chave: ChaveVtme,
  aditivo: ColetaVtmeLado,
  renegociacao: ColetaVtmeLado,
  mesmoPedido: boolean,
): ItemResumoQualidade {
  const regra = REGRAS_VTME[chave];
  if (chave === 'negociacao' && mesmoPedido) {
    const id = (aditivo.id || renegociacao.id || '').trim();
    return item(regra, false, textoOkVtme(chave), textoNaoLado({ ...aditivo, id }, chave, true, 'Aditivo'));
  }

  const okAditivo = okDaRegra(aditivo, chave, mesmoPedido);
  const okReneg = okDaRegra(renegociacao, chave, mesmoPedido);
  if (okAditivo === true && okReneg === true) {
    return item(regra, true, textoOkVtme(chave), textoNaoLado(aditivo, chave, mesmoPedido, 'Aditivo'));
  }

  const divergentes: Array<{ lado: ColetaVtmeLado; tipo: TipoPedido }> = [];
  if (okAditivo === false) divergentes.push({ lado: aditivo, tipo: 'Aditivo' });
  if (okReneg === false) divergentes.push({ lado: renegociacao, tipo: 'Renegociação' });

  if (divergentes.length === 1) {
    const { lado, tipo } = divergentes[0];
    return {
      rotulo: regra.rotulo,
      texto: textoNaoLado(lado, chave, mesmoPedido, tipo),
      tom: 'divergente',
      descricao: regra.descricao,
    };
  }

  if (divergentes.length === 2) {
    const frases = divergentes.map(({ lado, tipo }) => textoNaoLado(lado, chave, mesmoPedido, tipo));
    if (frases[0] === frases[1]) {
      return item(regra, false, textoOkVtme(chave), frases[0]);
    }
    const corpoAditivo = corpoDaFrase(frases[0], 'Aditivo', aditivo.id);
    const corpoReneg = corpoDaFrase(frases[1], 'Renegociação', renegociacao.id);
    if (corpoAditivo && corpoAditivo === corpoReneg) {
      return item(regra, false, textoOkVtme(chave), frasePedidos(aditivo.id, renegociacao.id, corpoAditivo));
    }
    return {
      rotulo: regra.rotulo,
      itens: frases,
      tom: 'divergente',
      descricao: regra.descricao,
    };
  }

  return item(regra, undefined, textoOkVtme(chave), textoNaoLado(aditivo, chave, mesmoPedido, 'Aditivo'));
}
